import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Download, Music, Pause, Play, RotateCcw,
  Share2, SkipBack, SkipForward, Upload, Volume2, VolumeX, X,
} from "lucide-react";

// ── Filter presets ────────────────────────────────────────────────────────────

const VIDEO_FILTERS = [
  { id: "none",      label: "Original",  adj: {},                                                          hue: 180 },
  { id: "cinematic", label: "Cinematic", adj: { contrast: 22, saturation: -14, brightness: -8 },           hue: 210 },
  { id: "vivid",     label: "Vivid",     adj: { saturation: 32, contrast: 20, brightness: 8 },             hue: 150 },
  { id: "warm",      label: "Warm",      adj: { temperature: 32, saturation: 12, brightness: 6 },          hue: 30  },
  { id: "cool",      label: "Cool",      adj: { temperature: -28, saturation: 8, contrast: 14 },           hue: 210 },
  { id: "teal",      label: "Teal/Org",  adj: { temperature: 24, tint: -14, saturation: 28, contrast: 18 },hue: 175 },
  { id: "bw",        label: "B&W",       adj: { saturation: -100, contrast: 26, brightness: 4 },           hue: 0   },
  { id: "matte",     label: "Matte",     adj: { contrast: -22, saturation: -14, brightness: 9, fade: 32 }, hue: 90  },
  { id: "film",      label: "Film",      adj: { temperature: 14, saturation: -8, contrast: -6, brightness: 6, fade: 14 }, hue: 45 },
  { id: "drama",     label: "Drama",     adj: { contrast: 36, brightness: -12, saturation: -12 },          hue: 240 },
  { id: "golden",    label: "Golden Hr", adj: { temperature: 42, brightness: 8, contrast: 14, saturation: 24 }, hue: 38 },
  { id: "night",     label: "Night",     adj: { brightness: -22, contrast: 32, saturation: -12 },          hue: 220 },
];

// ── CSS filter builder ────────────────────────────────────────────────────────

function buildFilterStr(filterAdj, manualAdj) {
  const m = {};
  for (const [k, v] of Object.entries(filterAdj)) m[k] = (m[k] || 0) + v;
  for (const [k, v] of Object.entries(manualAdj))  m[k] = (m[k] || 0) + v;

  const bright = 100 + (m.brightness || 0);
  const cont   = 100 + (m.contrast   || 0);
  const sat    = 100 + (m.saturation || 0);
  const hue    = (m.tint || 0) * 0.35 + (m.temperature || 0) * 0.22;
  const sepia  = Math.min(0.85, Math.max(0, (m.fade || 0) * 0.013));

  const parts = [
    `brightness(${Math.max(10, bright).toFixed(1)}%)`,
    `contrast(${Math.max(10, cont).toFixed(1)}%)`,
    `saturate(${Math.max(0, sat).toFixed(1)}%)`,
    `hue-rotate(${hue.toFixed(1)}deg)`,
  ];
  if (sepia > 0.01) parts.push(`sepia(${sepia.toFixed(3)})`);
  return parts.join(" ");
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(sec) {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

const ADJ_SLIDERS = [
  { key: "brightness", label: "Brightness", min: -60, max: 60 },
  { key: "contrast",   label: "Contrast",   min: -60, max: 60 },
  { key: "saturation", label: "Saturation", min: -60, max: 60 },
  { key: "temperature",label: "Warmth",     min: -60, max: 60 },
  { key: "fade",       label: "Fade",       min: 0,   max: 60 },
];

const VID_TABS = [
  { id: "filters", label: "Filters" },
  { id: "adjust",  label: "Adjust"  },
  { id: "speed",   label: "Speed"   },
  { id: "audio",   label: "Audio"   },
  { id: "caption", label: "Caption" },
  { id: "export",  label: "Export"  },
];

// ── Waveform decoration bars (stable reference) ────────────────────────────────
const WF_BARS = Array.from({ length: 72 }, (_, i) => ({
  key: i,
  h: `${22 + Math.abs(Math.sin(i * 1.618 + 1.1) * 16) + Math.abs(Math.cos(i * 2.7 + 0.3) * 10)}%`,
}));

// ── Main component ────────────────────────────────────────────────────────────

export default function VideoEditor({ photo, onSaved }) {
  const blob = photo?.editedBlob || photo?.blob;
  const [url,         setUrl]         = useState("");
  const [posterFrame, setPosterFrame] = useState(null);

  const videoRef    = useRef(null);
  const timelineRef = useRef(null);
  const rafRef      = useRef(null);
  const dragging    = useRef(null);
  const bgAudioRef  = useRef(null);
  const bgFileRef   = useRef(null);

  // Player state
  const [playing,     setPlaying]     = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(0);
  const [volume,      setVolume]      = useState(1);
  const [muted,       setMuted]       = useState(false);
  const [speed,       setSpeed]       = useState(1);

  // Trim
  const [trimIn,     setTrimIn]     = useState(0);
  const [trimOutRaw, setTrimOutRaw] = useState(null); // null = end of video
  const trimOut = trimOutRaw ?? duration;

  // Colour
  const [activeFilter, setActiveFilter] = useState("none");
  const [adj, setAdj] = useState({ brightness: 0, contrast: 0, saturation: 0, temperature: 0, fade: 0 });

  // Caption
  const [caption,     setCaption]     = useState("");
  const [captionPos,  setCaptionPos]  = useState("bottom");
  const [captionSize, setCaptionSize] = useState(2);

  // Background audio
  const [bgFile,     setBgFile]     = useState(null);
  const [bgAudioUrl, setBgAudioUrl] = useState("");
  const [bgName,     setBgName]     = useState("");
  const [bgVolume,   setBgVolume]   = useState(70);   // 0-100
  const [bgLoop,     setBgLoop]     = useState(true);
  const [bgFadeIn,   setBgFadeIn]   = useState(false);
  const [bgFadeOut,  setBgFadeOut]  = useState(false);

  // UI
  const [tab,        setTab]        = useState("filters");
  const [panelOpen,  setPanelOpen]  = useState(true);
  const [exporting,  setExporting]  = useState(false);
  const [exportProg, setExportProg] = useState(0);

  // ── URL from blob ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!blob) return;
    const u = URL.createObjectURL(blob);
    setUrl(u);
    setCurrentTime(0);
    setTrimIn(0);
    setTrimOutRaw(null);
    setPlaying(false);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  // ── Background audio URL ────────────────────────────────────────────────────
  useEffect(() => {
    if (!bgFile) return;
    const u = URL.createObjectURL(bgFile);
    setBgAudioUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [bgFile]);

  // ── Sync bg audio volume ──────────────────────────────────────────────────────
  useEffect(() => {
    const a = bgAudioRef.current;
    if (a) a.volume = bgVolume / 100;
  }, [bgVolume]);

  // ── Sync bg audio loop ────────────────────────────────────────────────────────
  useEffect(() => {
    const a = bgAudioRef.current;
    if (a) a.loop = bgLoop;
  }, [bgLoop]);

  // ── Sync bg audio play / pause with video ────────────────────────────────────
  useEffect(() => {
    const a = bgAudioRef.current;
    if (!a || !bgAudioUrl) return;
    if (playing) { a.play().catch(() => {}); }
    else          { a.pause(); }
  }, [playing, bgAudioUrl]);

  // ── Poster frame (for filter thumbnails) ─────────────────────────────────────
  useEffect(() => {
    if (!url) return;
    const v = document.createElement("video");
    v.src   = url;
    v.muted = true;
    v.crossOrigin = "anonymous";
    v.preload = "metadata";
    const capture = () => {
      try {
        const c = document.createElement("canvas");
        c.width = 144; c.height = 81;
        c.getContext("2d").drawImage(v, 0, 0, 144, 81);
        setPosterFrame(c.toDataURL("image/jpeg", 0.82));
      } catch { /* cross-origin or codec issue — thumbnails will use swatch */ }
      v.src = "";
    };
    v.addEventListener("seeked", capture, { once: true });
    v.addEventListener("loadedmetadata", () => { v.currentTime = 0.8; }, { once: true });
    v.load();
  }, [url]);

  // ── Sync volume / mute / speed to video element ───────────────────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = volume;
    v.muted  = muted;
  }, [volume, muted]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = speed;
  }, [speed]);

  // ── RAF-based time tracking + trim-out enforcement ────────────────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playing) return;
    const tick = () => {
      if (!videoRef.current) return;
      const t = videoRef.current.currentTime;
      setCurrentTime(t);
      if (trimOut > 0 && t >= trimOut - 0.05) {
        videoRef.current.pause();
        setPlaying(false);
        videoRef.current.currentTime = trimOut;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, trimOut]);

  // ── Filter string (memoised) ──────────────────────────────────────────────────
  const filterStr = useMemo(() => {
    const fa = VIDEO_FILTERS.find(f => f.id === activeFilter)?.adj ?? {};
    return buildFilterStr(fa, adj);
  }, [activeFilter, adj]);

  // ── Playback ──────────────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause(); setPlaying(false);
    } else {
      if (trimOut > 0 && v.currentTime >= trimOut - 0.05) v.currentTime = trimIn;
      v.play().catch(() => {});
      setPlaying(true);
    }
  }, [playing, trimIn, trimOut]);

  const seekTo = useCallback((t) => {
    const v = videoRef.current;
    if (!v) return;
    const clamped = Math.max(0, Math.min(duration || 0, t));
    v.currentTime = clamped;
    setCurrentTime(clamped);
  }, [duration]);

  const skipBack    = () => seekTo(Math.max(trimIn, currentTime - 5));
  const skipForward = () => seekTo(Math.min(trimOut, currentTime + 5));

  // ── Timeline pointer events ───────────────────────────────────────────────────
  const getTimeFromX = (clientX) => {
    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect || !duration) return 0;
    return Math.max(0, Math.min(duration, ((clientX - rect.left) / rect.width) * duration));
  };

  const handleTimelineDown = (e) => {
    if (!duration) return;
    const rect   = timelineRef.current.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const w      = rect.width;
    const inPx   = (trimIn  / duration) * w;
    const outPx  = (trimOut / duration) * w;
    const GRAB   = 22;

    if      (Math.abs(localX - inPx)  < GRAB) dragging.current = "in";
    else if (Math.abs(localX - outPx) < GRAB) dragging.current = "out";
    else { dragging.current = "seek"; seekTo(getTimeFromX(e.clientX)); }

    timelineRef.current.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const handleTimelineMove = (e) => {
    if (!dragging.current) return;
    const t = getTimeFromX(e.clientX);
    if      (dragging.current === "in")   setTrimIn(Math.max(0, Math.min(trimOut - 0.5, t)));
    else if (dragging.current === "out")  setTrimOutRaw(Math.max(trimIn + 0.5, Math.min(duration, t)));
    else                                  seekTo(t);
  };

  const handleTimelineUp = () => { dragging.current = null; };

  // ── Export (canvas capture + MediaRecorder) ────────────────────────────────────
  const doExport = async () => {
    const v = videoRef.current;
    if (!v || !blob || exporting) return;
    setExporting(true);
    setExportProg(0);

    try {
      const hasFilter = activeFilter !== "none" || Object.values(adj).some(x => x !== 0);
      const hasTrim   = trimIn > 0.05 || (trimOutRaw !== null && trimOut < duration - 0.05);
      const hasSpeed  = speed !== 1;

      // Fast path — no changes at all
      if (!hasFilter && !hasTrim && !hasSpeed) {
        const a = document.createElement("a");
        a.href = url; a.download = `lenspro-video-${Date.now()}.mp4`; a.click();
        setExporting(false); return;
      }

      // Canvas path
      const canvas   = document.createElement("canvas");
      canvas.width   = v.videoWidth  || 1280;
      canvas.height  = v.videoHeight || 720;
      const ctx      = canvas.getContext("2d");

      const mimeType = ["video/webm;codecs=vp9", "video/webm", "video/mp4"]
        .find(m => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } })
        || "video/webm";

      const canvasStream = canvas.captureStream(30);

      // ── Audio mixing ───────────────────────────────────────────────────────
      let audioCtxInst = null;
      try {
        if (bgAudioUrl) {
          // Mix original video audio + background music via Web Audio API
          const audioCtx = new AudioContext();
          audioCtxInst  = audioCtx;
          const dest    = audioCtx.createMediaStreamDestination();

          // Original video audio (skip if muted)
          if (!muted) {
            try {
              const vsStream = v.captureStream?.();
              if (vsStream?.getAudioTracks().length > 0) {
                const origSrc  = audioCtx.createMediaStreamSource(vsStream);
                const origGain = audioCtx.createGain();
                origGain.gain.value = volume;
                origSrc.connect(origGain);
                origGain.connect(dest);
              }
            } catch { /* video captureStream not available */ }
          }

          // Background audio — decode from blob URL
          const resp = await fetch(bgAudioUrl);
          const ab   = await resp.arrayBuffer();
          const buf  = await audioCtx.decodeAudioData(ab);
          const bgSrc  = audioCtx.createBufferSource();
          bgSrc.buffer = buf;
          bgSrc.loop   = bgLoop;
          bgSrc.playbackRate.value = speed;

          const bgGain = audioCtx.createGain();
          bgGain.gain.value = bgVolume / 100;

          // Fade-in envelope
          if (bgFadeIn) {
            const fadeDur = Math.min(2, (trimOut - trimIn) * 0.15);
            bgGain.gain.setValueAtTime(0, audioCtx.currentTime);
            bgGain.gain.linearRampToValueAtTime(bgVolume / 100, audioCtx.currentTime + fadeDur);
          }
          // Fade-out envelope
          if (bgFadeOut) {
            const clipLen = trimOut - trimIn;
            const fadeDur = Math.min(2, clipLen * 0.15);
            bgGain.gain.setValueAtTime(bgVolume / 100, audioCtx.currentTime + clipLen - fadeDur);
            bgGain.gain.linearRampToValueAtTime(0, audioCtx.currentTime + clipLen);
          }

          bgSrc.connect(bgGain);
          bgGain.connect(dest);
          bgSrc.start(0);

          const mixedTrack = dest.stream.getAudioTracks()[0];
          if (mixedTrack) canvasStream.addTrack(mixedTrack);

        } else {
          // No background audio — use original video audio track only
          const vsStream = v.captureStream?.();
          if (vsStream) {
            const aTrack = vsStream.getAudioTracks()[0];
            if (aTrack) canvasStream.addTrack(aTrack);
          }
        }
      } catch { /* audio mixing not supported on this device */ }

      const recorder = new MediaRecorder(canvasStream, { mimeType, videoBitsPerSecond: 8_000_000 });
      const chunks   = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };

      const finished = new Promise(res => { recorder.onstop = res; });

      // Seek to trim-in point
      v.currentTime = trimIn;
      await new Promise(res => v.addEventListener("seeked", res, { once: true }));

      v.playbackRate = speed;
      recorder.start(80);
      v.play().catch(() => {});

      const clipLen = Math.max(0.1, trimOut - trimIn);
      let   active  = true;

      const draw = () => {
        if (!active) return;
        const ct = v.currentTime;
        if (ct >= trimOut - 0.04 || v.ended || v.paused) {
          active = false; v.pause(); recorder.stop(); return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (hasFilter) ctx.filter = filterStr;
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        setExportProg(Math.min(99, Math.round(((ct - trimIn) / clipLen) * 100)));
        requestAnimationFrame(draw);
      };
      requestAnimationFrame(draw);

      await finished;
      setExportProg(100);

      const ext        = mimeType.includes("mp4") ? "mp4" : "webm";
      const exportBlob = new Blob(chunks, { type: mimeType });
      const eUrl       = URL.createObjectURL(exportBlob);
      const a          = document.createElement("a");
      a.href = eUrl; a.download = `lenspro-video-${Date.now()}.${ext}`; a.click();
      setTimeout(() => URL.revokeObjectURL(eUrl), 10_000);

    } catch (err) {
      console.error("Video export error:", err);
      // Fallback — download original
      const a = document.createElement("a");
      a.href = url; a.download = `lenspro-video-${Date.now()}.mp4`; a.click();
    } finally {
      audioCtxInst?.close().catch(() => {});
      setExporting(false);
      setExportProg(0);
    }
  };

  const doShare = async () => {
    if (!navigator.share || !blob) return;
    try {
      await navigator.share({
        files: [new File([blob], "lenspro-video.mp4", { type: blob.type || "video/mp4" })],
        title: "LensPro Video",
      });
    } catch { /* user cancelled or unsupported */ }
  };

  if (!blob) return null;

  // ── Derived values ────────────────────────────────────────────────────────────
  const trimInPct  = duration ? (trimIn    / duration) * 100 : 0;
  const trimOutPct = duration ? (trimOut   / duration) * 100 : 100;
  const playPct    = duration ? (currentTime / duration) * 100 : 0;
  const hasTrim    = trimIn > 0.05 || (trimOutRaw !== null && trimOut < duration - 0.05);
  const trimmedDur = trimOut - trimIn;

  return (
    <section className="ve-page">

      {/* ── Header ── */}
      <header className="ve-header safe-top">
        <div className="ve-title">
          <span className="ve-title-icon">▶</span>
          <strong>Video Studio</strong>
          {hasTrim && <span className="ve-trim-badge">{fmt(trimmedDur)}</span>}
        </div>
        <div className="ve-header-right">
          <button type="button" className="st-compare-btn" onClick={doShare}>
            Share
          </button>
          <button
            type="button"
            className="primary-button small"
            onClick={doExport}
            disabled={exporting}
          >
            <Download size={14} />
            {exporting ? `${exportProg}%` : "Export"}
          </button>
        </div>
      </header>

      {/* ── Video preview ── */}
      <div className="ve-preview-wrap" onClick={togglePlay}>
        <div className="ve-preview-inner">
          <video
            ref={videoRef}
            src={url}
            className="ve-video"
            style={{ filter: filterStr }}
            playsInline
            preload="metadata"
            onLoadedMetadata={() => {
              const v = videoRef.current;
              if (v) { setDuration(v.duration); setTrimOutRaw(null); }
            }}
            onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
            onPlay={()  => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
          />

          {/* Caption overlay */}
          {caption && (
            <div className={`ve-caption ve-caption-${captionPos} ve-caption-s${captionSize}`}>
              {caption}
            </div>
          )}

          {/* Play/pause overlay */}
          <div className={`ve-play-overlay${playing ? " is-playing" : ""}`}>
            <div className="ve-play-circle">
              {playing ? <Pause size={28} /> : <Play size={28} fill="currentColor" />}
            </div>
          </div>
        </div>

        {/* Time row (below video) */}
        <div className="ve-time-row">
          <span className="ve-time">{fmt(currentTime)}</span>
          <span className="ve-time-sep">/</span>
          <span className="ve-time ve-time-dur">{fmt(trimOut)}</span>
          {speed !== 1 && <span className="ve-speed-chip">{speed}×</span>}
        </div>
      </div>

      {/* ── Bottom panel ── */}
      <div className={`ve-panel${panelOpen ? "" : " is-collapsed"}`}>

        {/* Drag handle — tap to collapse/expand */}
        <button
          type="button"
          className="ve-panel-toggle"
          onClick={() => setPanelOpen(p => !p)}
          aria-label={panelOpen ? "Collapse panel" : "Expand panel"}
        >
          <span className="ve-panel-pill" />
        </button>

        {/* ── Timeline ── */}
        <div className="ve-timeline-wrap">
          <div
            ref={timelineRef}
            className="ve-timeline"
            onPointerDown={handleTimelineDown}
            onPointerMove={handleTimelineMove}
            onPointerUp={handleTimelineUp}
            onPointerCancel={handleTimelineUp}
          >
            {/* Waveform decoration */}
            <div className="ve-waveform" aria-hidden="true">
              {WF_BARS.map(({ key, h }) => (
                <div key={key} className="ve-wf-bar" style={{ height: h }} />
              ))}
            </div>

            {/* Trim shade — region before in-point */}
            <div className="ve-trim-shade left"  style={{ width: `${trimInPct}%` }} />
            {/* Trim shade — region after out-point */}
            <div className="ve-trim-shade right" style={{ left: `${trimOutPct}%`, right: 0 }} />

            {/* Active region highlight */}
            <div
              className="ve-trim-active"
              style={{ left: `${trimInPct}%`, width: `${trimOutPct - trimInPct}%` }}
            />

            {/* Playhead */}
            <div className="ve-playhead" style={{ left: `${playPct}%` }}>
              <div className="ve-playhead-knob" />
            </div>

            {/* Trim handle — in point */}
            <div className="ve-trim-handle in" style={{ left: `${trimInPct}%` }}>
              <div className="ve-trim-handle-grip">◀</div>
            </div>
            {/* Trim handle — out point */}
            <div className="ve-trim-handle out" style={{ left: `${trimOutPct}%` }}>
              <div className="ve-trim-handle-grip">▶</div>
            </div>
          </div>

          {/* Trim time labels */}
          <div className="ve-trim-labels">
            <span>{fmt(trimIn)}</span>
            <span className="ve-total-dur">{fmt(duration)}</span>
            <span>{fmt(trimOut)}</span>
          </div>
        </div>

        {/* ── Playback controls ── */}
        <div className="ve-controls">
          <button className="ve-ctrl" type="button" onClick={skipBack}    title="−5 s"><SkipBack    size={18} /></button>
          <button className="ve-ctrl ve-ctrl-play" type="button" onClick={togglePlay}>
            {playing ? <Pause size={24} /> : <Play size={24} fill="currentColor" />}
          </button>
          <button className="ve-ctrl" type="button" onClick={skipForward} title="+5 s"><SkipForward size={18} /></button>
          <button
            className={`ve-ctrl${muted ? " is-active" : ""}`}
            type="button"
            onClick={() => setMuted(v => !v)}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <button
            className={`ve-ctrl${hasTrim ? " is-active" : ""}`}
            type="button"
            title="Reset trim"
            onClick={() => { setTrimIn(0); setTrimOutRaw(null); seekTo(0); }}
          >
            <RotateCcw size={16} />
          </button>
        </div>

        {/* ── Tab bar ── */}
        <div className="ve-tabs">
          {VID_TABS.map(({ id, label: lbl }) => (
            <button
              key={id}
              type="button"
              className={`ve-tab${tab === id ? " is-active" : ""}`}
              onClick={() => setTab(id)}
            >
              {lbl}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="ve-content">

          {/* ─ Filters ─ */}
          {tab === "filters" && (
            <div className="ve-filter-scroll">
              {VIDEO_FILTERS.map((f) => {
                const fStr = buildFilterStr(f.adj, {});
                return (
                  <button
                    key={f.id}
                    type="button"
                    className={`ve-filter-card${activeFilter === f.id ? " is-active" : ""}`}
                    onClick={() => setActiveFilter(f.id)}
                  >
                    <div className="ve-filter-thumb">
                      {posterFrame
                        ? <img src={posterFrame} alt="" className="ve-filter-img" style={{ filter: fStr }} />
                        : <div className="ve-filter-swatch" style={{ background: `hsl(${f.hue},30%,16%)`, filter: fStr }} />
                      }
                      {activeFilter === f.id && <div className="ve-filter-check">✓</div>}
                    </div>
                    <span className="ve-filter-label">{f.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ─ Adjust ─ */}
          {tab === "adjust" && (
            <div className="ve-adj-group">
              {ADJ_SLIDERS.map(({ key, label: lbl, min, max }) => {
                const v = adj[key] || 0;
                return (
                  <div key={key} className="ve-adj-row">
                    <div className="ve-adj-head">
                      <span className="ve-adj-label">{lbl}</span>
                      <span className={`ve-adj-val${v !== 0 ? " nonzero" : ""}`}>
                        {v > 0 ? `+${v}` : v}
                      </span>
                      {v !== 0 && (
                        <button
                          type="button"
                          className="ve-adj-reset"
                          onClick={() => setAdj(a => ({ ...a, [key]: 0 }))}
                        >✕</button>
                      )}
                    </div>
                    <input
                      type="range" min={min} max={max} value={v}
                      className="st-range"
                      onChange={e => setAdj(a => ({ ...a, [key]: Number(e.target.value) }))}
                    />
                  </div>
                );
              })}
              <button
                type="button"
                className="ve-reset-btn"
                onClick={() => setAdj({ brightness: 0, contrast: 0, saturation: 0, temperature: 0, fade: 0 })}
              >
                <RotateCcw size={13} /> Reset adjustments
              </button>
            </div>
          )}

          {/* ─ Speed ─ */}
          {tab === "speed" && (
            <div className="ve-speed-panel">
              <div className="ve-section-lbl">Playback Speed</div>
              <div className="ve-speed-grid">
                {SPEED_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`ve-speed-btn${speed === s ? " is-active" : ""}`}
                    onClick={() => setSpeed(s)}
                  >
                    <span className="ve-speed-num">{s}</span>
                    <span className="ve-speed-x">×</span>
                  </button>
                ))}
              </div>
              <p className="ve-panel-note">Speed applies to both preview and export.</p>

              <div className="ve-section-lbl" style={{ marginTop: 20 }}>Trim Points</div>
              <div className="ve-trim-rows">
                {[
                  { lbl: "In point",  val: fmt(trimIn),        action: () => setTrimIn(Math.min(currentTime, Math.max(0, trimOut - 0.5))) },
                  { lbl: "Out point", val: fmt(trimOut),       action: () => setTrimOutRaw(Math.max(currentTime, trimIn + 0.5)) },
                  { lbl: "Duration",  val: fmt(trimOut - trimIn), accent: true },
                ].map(({ lbl, val, action, accent }) => (
                  <div key={lbl} className="ve-trim-row">
                    <span className="ve-trim-row-lbl">{lbl}</span>
                    <span className={`ve-trim-row-val${accent ? " accent" : ""}`}>{val}</span>
                    {action && (
                      <button type="button" className="ve-trim-set" onClick={action}>
                        Set here
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─ Audio ─ */}
          {tab === "audio" && (
            <div className="ve-audio-panel">

              {/* ── Original audio ── */}
              <div className="ve-section-lbl">Original Audio</div>
              <div className="ve-vol-row">
                <button
                  type="button"
                  className={`ve-ctrl small${muted ? " is-active" : ""}`}
                  onClick={() => setMuted(v => !v)}
                  title={muted ? "Unmute" : "Mute original"}
                >
                  {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
                </button>
                <input
                  type="range" min={0} max={1} step={0.02}
                  value={muted ? 0 : volume}
                  className="st-range ve-vol-slider"
                  onChange={e => { setVolume(Number(e.target.value)); setMuted(false); }}
                />
                <span className="ve-adj-val">{Math.round((muted ? 0 : volume) * 100)}%</span>
              </div>

              {/* ── Background music ── */}
              <div className="ve-section-lbl" style={{ marginTop: 20 }}>Background Music</div>

              {!bgAudioUrl ? (
                /* Upload zone */
                <button
                  type="button"
                  className="ve-audio-upload-zone"
                  onClick={() => bgFileRef.current?.click()}
                >
                  <span className="ve-audio-upload-ring">
                    <Music size={22} />
                  </span>
                  <span className="ve-audio-upload-title">Add Background Music</span>
                  <span className="ve-audio-upload-sub">Tap to browse · MP3, AAC, WAV, M4A, OGG</span>
                </button>
              ) : (
                /* Loaded track controls */
                <div className="ve-audio-loaded">

                  {/* File row */}
                  <div className="ve-audio-file-row">
                    <span className="ve-audio-file-dot" />
                    <Music size={14} className="ve-audio-file-icon" />
                    <span className="ve-audio-file-name">{bgName || "Background music"}</span>
                    <button
                      type="button"
                      className="ve-audio-file-remove"
                      onClick={() => { setBgFile(null); setBgAudioUrl(""); setBgName(""); }}
                      title="Remove track"
                    >
                      <X size={13} />
                    </button>
                  </div>

                  {/* Replace button */}
                  <button
                    type="button"
                    className="ve-audio-replace-btn"
                    onClick={() => bgFileRef.current?.click()}
                  >
                    <Upload size={13} /> Replace track
                  </button>

                  {/* BG volume */}
                  <div className="ve-section-lbl" style={{ marginTop: 14 }}>Music Volume</div>
                  <div className="ve-vol-row">
                    <Music size={15} style={{ color: "var(--accent)", flexShrink: 0 }} />
                    <input
                      type="range" min={0} max={100}
                      value={bgVolume}
                      className="st-range ve-vol-slider"
                      onChange={e => setBgVolume(Number(e.target.value))}
                    />
                    <span className="ve-adj-val nonzero">{bgVolume}%</span>
                  </div>

                  {/* Options — Loop, Fade In, Fade Out */}
                  <div className="ve-section-lbl" style={{ marginTop: 14 }}>Options</div>
                  <div className="ve-audio-opts">
                    {[
                      { key: "loop",    label: "Loop music",   desc: "Repeat if shorter than video", val: bgLoop,    set: setBgLoop    },
                      { key: "fadeIn",  label: "Fade in",      desc: "Gradually increase at start",  val: bgFadeIn,  set: setBgFadeIn  },
                      { key: "fadeOut", label: "Fade out",     desc: "Gradually decrease at end",    val: bgFadeOut, set: setBgFadeOut },
                    ].map(({ key, label, desc, val, set }) => (
                      <div key={key} className="ve-toggle-row">
                        <div className="ve-toggle-text">
                          <span className="ve-toggle-label">{label}</span>
                          <span className="ve-toggle-desc">{desc}</span>
                        </div>
                        <button
                          type="button"
                          className={`ve-toggle${val ? " on" : ""}`}
                          onClick={() => set(v => !v)}
                          aria-pressed={val}
                        >
                          <span className="ve-toggle-knob" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="ve-panel-note" style={{ marginTop: 12 }}>
                Background music is mixed with the video during export.
              </p>
            </div>
          )}

          {/* ─ Caption ─ */}
          {tab === "caption" && (
            <div className="ve-caption-editor">
              <div className="ve-section-lbl">Caption Text</div>
              <textarea
                className="st-text-input"
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="Add a caption to your video…"
                maxLength={120}
                rows={3}
              />
              {caption && (
                <>
                  <div className="ve-section-lbl" style={{ marginTop: 14 }}>Size</div>
                  <div className="st-size-row">
                    {["S", "M", "L"].map((s, i) => (
                      <button
                        key={s} type="button"
                        className={`st-size-btn${captionSize === i + 1 ? " is-active" : ""}`}
                        onClick={() => setCaptionSize(i + 1)}
                      >{s}</button>
                    ))}
                  </div>
                  <div className="ve-section-lbl" style={{ marginTop: 14 }}>Position</div>
                  <div className="st-pos-row">
                    {["top", "center", "bottom"].map(p => (
                      <button
                        key={p} type="button"
                        className={`st-pos-btn${captionPos === p ? " is-active" : ""}`}
                        onClick={() => setCaptionPos(p)}
                      >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ─ Export ─ */}
          {tab === "export" && (
            <div className="ve-export-panel">
              <div className="ve-section-lbl">Summary</div>
              <div className="ve-export-summary">
                {[
                  ["Duration", fmt(trimOut - trimIn)],
                  ["Speed",    `${speed}×`],
                  ["Filter",   VIDEO_FILTERS.find(f => f.id === activeFilter)?.label ?? "None"],
                  ["Music",    bgName || (bgAudioUrl ? "Added" : "None")],
                  ["Output",   "WebM / MP4"],
                ].map(([k, v]) => (
                  <div key={k} className="ve-export-row">
                    <span>{k}</span>
                    <span className={`ve-export-val${k === "Music" && bgAudioUrl ? " accent-val" : ""}`}>{v}</span>
                  </div>
                ))}
              </div>

              {exporting && (
                <div className="ve-export-progress-wrap">
                  <div className="ve-export-track">
                    <div className="ve-export-fill" style={{ width: `${exportProg}%` }} />
                  </div>
                  <span className="ve-export-pct">Exporting… {exportProg}%</span>
                </div>
              )}

              <div className="ve-export-btns">
                <button
                  type="button"
                  className="primary-button"
                  onClick={doExport}
                  disabled={exporting}
                >
                  <Download size={15} />
                  {exporting ? `Exporting ${exportProg}%` : "Export Video"}
                </button>
                <button type="button" className="st-export-btn ghost" onClick={doShare}>
                  <Share2 size={14} /> Share
                </button>
                <button
                  type="button"
                  className="st-export-btn ghost"
                  onClick={() => { const a = document.createElement("a"); a.href = url; a.download = `lenspro-original-${Date.now()}.mp4`; a.click(); }}
                >
                  <Download size={14} /> Download Original
                </button>
              </div>
            </div>
          )}

        </div>{/* /ve-content */}
      </div>{/* /ve-panel */}

      {/* Hidden bg audio element — persists regardless of active tab */}
      {bgAudioUrl && (
        <audio
          ref={bgAudioRef}
          src={bgAudioUrl}
          loop={bgLoop}
          preload="auto"
          style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
        />
      )}

      {/* Hidden file input for bg audio upload */}
      <input
        ref={bgFileRef}
        type="file"
        accept="audio/*,.mp3,.wav,.aac,.m4a,.ogg,.flac"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          setBgFile(f);
          setBgName(f.name.replace(/\.[^.]+$/, ""));
          e.target.value = "";
        }}
      />
    </section>
  );
}
